/**
 * useObjectDetection — TensorFlow.js COCO-SSD 기반 객체 감지 composable
 *
 * video 엘리먼트에서 프레임을 캡처하여 객체를 감지하고,
 * canvas 오버레이에 바운딩 박스 + 라벨을 그린다.
 */

import { ref, watch, type Ref } from 'vue'

interface Detection {
  bbox: [number, number, number, number]  // [x, y, width, height]
  class: string
  score: number
}

interface CocoSsdModel {
  detect: (img: HTMLVideoElement | HTMLCanvasElement, maxDetections?: number, minScore?: number) => Promise<Detection[]>
  dispose: () => void
}

// 모델 싱글턴 (한 번만 로드)
let modelPromise: Promise<CocoSsdModel> | null = null
let modelInstance: CocoSsdModel | null = null

async function loadModel(): Promise<CocoSsdModel> {
  if (modelInstance) return modelInstance
  if (modelPromise) return modelPromise

  modelPromise = (async () => {
    // @tensorflow/tfjs + @tensorflow-models/coco-ssd dynamic import
    const [tf, cocoSsd] = await Promise.all([
      import('@tensorflow/tfjs'),
      import('@tensorflow-models/coco-ssd'),
    ])
    await tf.ready()
    console.log('[ObjectDetection] TF.js 백엔드:', tf.getBackend())
    const model = await cocoSsd.load({ base: 'lite_mobilenet_v2' })
    console.log('[ObjectDetection] COCO-SSD 모델 로드 완료')
    modelInstance = model as unknown as CocoSsdModel
    return modelInstance
  })()

  return modelPromise
}

// 바운딩 박스 색상 팔레트
const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF']

function getColor(className: string): string {
  let hash = 0
  for (let i = 0; i < className.length; i++) hash = className.charCodeAt(i) + ((hash << 5) - hash)
  return COLORS[Math.abs(hash) % COLORS.length]
}

export function useObjectDetection(
  videoRef: Ref<HTMLVideoElement | null>,
  canvasRef: Ref<HTMLCanvasElement | null>,
  enabled: Ref<boolean>,
  opts?: { fps?: number; minScore?: number; maxDetections?: number }
) {
  const fps = opts?.fps ?? 5
  const minScore = opts?.minScore ?? 0.5
  const maxDetections = opts?.maxDetections ?? 20

  const loading = ref(false)
  const detections = ref<Detection[]>([])
  const error = ref<string | null>(null)

  let animFrameId: number | null = null
  let lastDetectTime = 0
  let model: CocoSsdModel | null = null
  const detectInterval = 1000 / fps

  async function startDetection() {
    if (model) return
    loading.value = true
    error.value = null

    try {
      model = await loadModel()
    } catch (err: any) {
      console.error('[ObjectDetection] 모델 로드 실패:', err)
      error.value = err.message || '모델 로드 실패'
      loading.value = false
      return
    }

    loading.value = false
    requestDetectionFrame()
  }

  function stopDetection() {
    if (animFrameId !== null) {
      cancelAnimationFrame(animFrameId)
      animFrameId = null
    }
    detections.value = []
    clearCanvas()
    // 모델은 싱글턴이므로 dispose하지 않음
    model = null
  }

  function requestDetectionFrame() {
    animFrameId = requestAnimationFrame(detectLoop)
  }

  async function detectLoop(timestamp: number) {
    if (!enabled.value || !model) {
      stopDetection()
      return
    }

    const video = videoRef.value
    const canvas = canvasRef.value
    if (!video || !canvas || video.readyState < 2) {
      requestDetectionFrame()
      return
    }

    if (timestamp - lastDetectTime >= detectInterval) {
      lastDetectTime = timestamp

      try {
        const results = await model.detect(video, maxDetections, minScore)
        detections.value = results
        drawDetections(canvas, video, results)
      } catch {
        // 감지 실패 (비디오 상태 변경 등) — 무시
      }
    }

    requestDetectionFrame()
  }

  function drawDetections(canvas: HTMLCanvasElement, video: HTMLVideoElement, dets: Detection[]) {
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // canvas 크기를 video 표시 크기에 맞춤
    const rect = video.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // video 실제 해상도 → canvas 표시 크기 비율
    const scaleX = canvas.width / video.videoWidth
    const scaleY = canvas.height / video.videoHeight

    for (const det of dets) {
      const [x, y, w, h] = det.bbox
      const sx = x * scaleX
      const sy = y * scaleY
      const sw = w * scaleX
      const sh = h * scaleY
      const color = getColor(det.class)

      // 바운딩 박스
      ctx.strokeStyle = color
      ctx.lineWidth = 2
      ctx.strokeRect(sx, sy, sw, sh)

      // 라벨 배경
      const label = `${det.class} ${(det.score * 100).toFixed(0)}%`
      ctx.font = '12px sans-serif'
      const textWidth = ctx.measureText(label).width
      ctx.fillStyle = color
      ctx.fillRect(sx, sy - 16, textWidth + 6, 16)

      // 라벨 텍스트
      ctx.fillStyle = '#fff'
      ctx.fillText(label, sx + 3, sy - 4)
    }
  }

  function clearCanvas() {
    const canvas = canvasRef.value
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  // enabled 변경 감시
  watch(enabled, (newVal) => {
    if (newVal) {
      startDetection()
    } else {
      stopDetection()
    }
  })

  return { loading, detections, error }
}
